import { useEffect, useState } from 'react'
import { IBKR_TF, toCandles } from '../lib/candleStore.js'
import { supabase } from '../lib/supabase.js'

const ADJUSTED_DAILY_FETCH_TIMEOUT_MS = 95 * 1000

const CARS_ADJUSTED_DAILY_DURATION = '5 Y'
const CARS_ADJUSTED_WHAT_TO_SHOW = 'ADJUSTED_LAST'

function readIbkrDayKey(rawTime) {
  if (typeof rawTime === 'string' && /^\d{8}$/.test(rawTime)) return rawTime
  if (
    typeof rawTime === 'number'
    && Number.isFinite(rawTime)
    && Number.isInteger(rawTime)
    && rawTime >= 19000101
    && rawTime <= 99991231
  ) {
    return String(rawTime)
  }
  return null
}

async function fetchAdjustedDailyBars(symbol, timezone, authHeader, signal) {
  const params = new URLSearchParams({
    symbol,
    barSize: IBKR_TF.daily.barSize,
    duration: CARS_ADJUSTED_DAILY_DURATION,
    whatToShow: CARS_ADJUSTED_WHAT_TO_SHOW,
    useRTH: '1',
  })

  const response = await fetch(`/api/ibkr/candles?${params}`, {
    signal,
    headers: { Authorization: authHeader },
  })
  if (!response.ok) throw new Error(`CA|RS adjusted daily fetch ${response.status}`)
  const data = await response.json()
  const rawBars = Array.isArray(data?.bars) ? data.bars : []
  const converted = toCandles(rawBars, timezone)
  return converted.map((bar, i) => {
    const ibkrDayKey = readIbkrDayKey(rawBars[i]?.time)
    return ibkrDayKey ? { ...bar, ibkrDayKey } : bar
  })
}

export function useCarsAdjustedDailySource(primarySymbol, benchmarkSymbol, enabled, timezone) {
  const [state, setState] = useState({
    primaryBars: [],
    benchmarkBars: [],
    status: 'idle',
  })

  useEffect(() => {
    if (!enabled || !primarySymbol || !benchmarkSymbol) {
      setState({ primaryBars: [], benchmarkBars: [], status: 'idle' })
      return
    }

    const controller = new AbortController()
    let didTimeoutAbort = false
    const timeout = setTimeout(() => {
      didTimeoutAbort = true
      controller.abort()
    }, ADJUSTED_DAILY_FETCH_TIMEOUT_MS)
    let cancelled = false
    setState({ primaryBars: [], benchmarkBars: [], status: 'loading' })

    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (cancelled) return
        if (!session?.access_token) throw new Error('CA|RS adjusted daily source: missing Supabase session')
        const authHeader = `Bearer ${session.access_token}`
        const [primaryBars, benchmarkBars] = await Promise.all([
          fetchAdjustedDailyBars(primarySymbol, timezone, authHeader, controller.signal),
          fetchAdjustedDailyBars(benchmarkSymbol, timezone, authHeader, controller.signal),
        ])
        clearTimeout(timeout)
        if (cancelled) return
        setState({ primaryBars, benchmarkBars, status: 'ready' })
      } catch (err) {
        clearTimeout(timeout)
        if (cancelled) return
        if (err?.name === 'AbortError' && !didTimeoutAbort) return
        setState({ primaryBars: [], benchmarkBars: [], status: 'error' })
      }
    })()

    return () => {
      cancelled = true
      clearTimeout(timeout)
      controller.abort()
    }
  }, [primarySymbol, benchmarkSymbol, enabled, timezone])

  return state
}
